// =============================================================================
// SEVRIC Chat — mutations
// -----------------------------------------------------------------------------
// All write paths through here. Each function does the minimum work and
// returns the new row so the caller can optimistically update UI before the
// realtime subscription bounces.
// =============================================================================
import { getSupabase } from "./client.js";

// =============================================================================
// sendMessage — post a message to a channel, DM, or entity thread.
// scope = { kind: 'channel'|'dm'|'entity_thread', id: uuid }
// author = { uid, displayName, initials }
// =============================================================================
export async function sendMessage({ scope, author, tenantId, body, mentions, attachments, replyTo, messageKind = "text" }) {
  if (!scope?.id || !author?.uid || !tenantId) {
    throw new Error("sendMessage: scope + author + tenantId required");
  }
  if (!body && (!attachments || attachments.length === 0) && messageKind === "text") {
    throw new Error("sendMessage: empty message");
  }
  const supa = getSupabase();
  const row = {
    tenant_id: tenantId,
    scope_kind: scope.kind,
    scope_id: scope.id,
    author_uid: author.uid,
    author_display_name: author.displayName || null,
    author_initials: author.initials || null,
    body: body || null,
    message_kind: messageKind,
    attachments: attachments || null,
    reply_to: replyTo || null,
    mentions: mentions || null,
  };
  const res = await supa.from("messages").insert(row).select().single();
  if (res.error) throw res.error;
  return res.data;
}

// =============================================================================
// createChannel — create a new team room. Caller becomes the first admin.
// =============================================================================
export async function createChannel({ tenantId, createdBy, name, slug, description, icon = "people", accent = "cobalt", isPrivate = false }) {
  if (!tenantId || !createdBy || !name) {
    throw new Error("createChannel: tenantId + createdBy + name required");
  }
  const supa = getSupabase();
  const finalSlug = (slug || name).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 48);
  const insertChannel = await supa.from("channels").insert({
    tenant_id: tenantId, created_by: createdBy, name, slug: finalSlug,
    description: description || null, icon, accent, is_private: isPrivate,
  }).select().single();
  if (insertChannel.error) throw insertChannel.error;
  const channel = insertChannel.data;
  // Add creator as admin.
  const insertMember = await supa.from("channel_members").insert({
    channel_id: channel.id, uid: createdBy, role: "admin",
  });
  if (insertMember.error) throw insertMember.error;
  return channel;
}

// =============================================================================
// openDm — find-or-create the canonical DM row between two users in a tenant.
// Sorts the uids lexicographically so the same pair always resolves to the
// same row (enforced by the schema's UNIQUE constraint).
// =============================================================================
export async function openDm({ tenantId, callerUid, otherUid }) {
  if (!tenantId || !callerUid || !otherUid) {
    throw new Error("openDm: tenantId + callerUid + otherUid required");
  }
  if (callerUid === otherUid) throw new Error("openDm: can't DM yourself");
  const [a, b] = [callerUid, otherUid].sort();
  const supa = getSupabase();
  // Try to read first.
  const existing = await supa.from("dms")
    .select("*").eq("tenant_id", tenantId)
    .eq("participant_a", a).eq("participant_b", b).maybeSingle();
  if (existing.data) return existing.data;
  // Insert.
  const isAi = (otherUid === "sevric-ai" || callerUid === "sevric-ai");
  const inserted = await supa.from("dms").insert({
    tenant_id: tenantId, participant_a: a, participant_b: b, is_ai: isAi,
  }).select().single();
  if (inserted.error) throw inserted.error;
  return inserted.data;
}

// =============================================================================
// openEntityThread — find-or-create a thread for a specific unit/vehicle/vendor.
// entityKind ∈ ('vehicle','battery_unit','vendor','form_submission','wine_item')
// =============================================================================
export async function openEntityThread({ tenantId, storeId, createdBy, entityKind, entityId, entityLabel }) {
  if (!tenantId || !createdBy || !entityKind || !entityId) {
    throw new Error("openEntityThread: tenantId + createdBy + entityKind + entityId required");
  }
  const supa = getSupabase();
  const existing = await supa.from("entity_threads")
    .select("*").eq("tenant_id", tenantId)
    .eq("entity_kind", entityKind).eq("entity_id", entityId).maybeSingle();
  if (existing.data) return existing.data;
  const inserted = await supa.from("entity_threads").insert({
    tenant_id: tenantId, store_id: storeId || null, created_by: createdBy,
    entity_kind: entityKind, entity_id: entityId, entity_label: entityLabel || null,
  }).select().single();
  if (inserted.error) throw inserted.error;
  return inserted.data;
}

// =============================================================================
// archiveThread — Storefront-driven archive. Used when a unit is sold or a
// vendor is retired. Calls the SECURITY DEFINER function `archive_entity_thread`
// which is the only path allowed by RLS.
// =============================================================================
export async function archiveThread({ tenantId, entityKind, entityId, reason = "manual" }) {
  const supa = getSupabase();
  const res = await supa.rpc("archive_entity_thread", {
    p_tenant_id: tenantId,
    p_entity_kind: entityKind,
    p_entity_id: entityId,
    p_reason: reason,
  });
  if (res.error) throw res.error;
}

// =============================================================================
// restoreThread — pull an archived thread back to active. Used when a sold
// car comes back through, or a closed vendor is re-engaged.
// =============================================================================
export async function restoreThread({ tenantId, entityKind, entityId }) {
  const supa = getSupabase();
  const res = await supa.rpc("restore_entity_thread", {
    p_tenant_id: tenantId,
    p_entity_kind: entityKind,
    p_entity_id: entityId,
  });
  if (res.error) throw res.error;
}

// =============================================================================
// markRead — bump the last_read_at timestamp for the caller on a scope.
// Used to compute unread counts.
// =============================================================================
export async function markRead({ uid, scope }) {
  if (!uid || !scope?.id) return;
  const supa = getSupabase();
  await supa.from("read_receipts").upsert({
    uid, scope_kind: scope.kind, scope_id: scope.id, last_read_at: new Date().toISOString(),
  });
}

// =============================================================================
// uploadPhoto / uploadVoice — Storage helpers. Paths follow
//   {tenant_id}/{scope_kind}/{scope_id}/{message_id}/{filename}
// so RLS can validate the caller's tenant from the path prefix.
// =============================================================================
export async function uploadPhoto({ tenantId, scope, messageId, file }) {
  const supa = getSupabase();
  const filename = sanitizeFilename(file.name || "photo.jpg");
  const path = `${tenantId}/${scope.kind}/${scope.id}/${messageId}/${filename}`;
  const up = await supa.storage.from("chat-photos").upload(path, file, {
    cacheControl: "31536000", upsert: false, contentType: file.type,
  });
  if (up.error) throw up.error;
  return { path, bucket: "chat-photos" };
}

export async function uploadVoice({ tenantId, scope, messageId, file }) {
  const supa = getSupabase();
  const filename = sanitizeFilename(file.name || "voice.m4a");
  const path = `${tenantId}/${scope.kind}/${scope.id}/${messageId}/${filename}`;
  const up = await supa.storage.from("chat-voice").upload(path, file, {
    cacheControl: "31536000", upsert: false, contentType: file.type,
  });
  if (up.error) throw up.error;
  return { path, bucket: "chat-voice" };
}

// Strip control characters + path traversal attempts. Keep names short.
function sanitizeFilename(name) {
  return String(name || "")
    .replace(/[\x00-\x1F\x7F]/g, "")
    .replace(/[\\/]/g, "-")
    .replace(/^\.+/, "")
    .slice(0, 80) || `file-${Date.now()}`;
}
